import { Component, OnInit, inject, DestroyRef, effect, Signal, computed, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    FieldFieldComponent,
    FieldMappingComponent,
    FieldRowComponent,
    InstructionsComponent,
} from '@r1-compass-library/components';
import { MatButtonModule } from '@angular/material/button';

import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { DynamicContentStore } from './dynamic-content.store';
import { PlayStore } from '../store/play.store';
import { RoutingService, SnackBarService } from '@r1-compass-library/services';
import { combineLatest, debounceTime, switchMap, map, filter, take, delay, fromEvent, merge, tap } from 'rxjs';
import { filterNil } from 'ngxtension/filter-nil';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    ContentAsset,
    DataMartMergeField,
    Play,
    PlayConfig,
    TalkingPointsDefinition,
} from '@r1-compass-library/models';
import { ContentEditorComponent } from '../../content-editor/content-editor.component';
import { PlayTalkingPointsForm, PlayTalkingPointsFormService } from './play-talking-points-form.service';
import { FieldMergeConstants } from '@r1-compass-library/constants/field-merge.constants';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { injectParams } from 'ngxtension/inject-params';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { FormUnsavedChangesWarningEntity } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { delegatedSignal } from '@r1-compass-library/utilities/delegated-signal';
import { PlayFormMap, PlayValidationContext, PlayValidationService } from '../play-validation.service';
import { PlayValidationContextService } from '../play-validation-context.service';
import {
    PlaySectionName,
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
} from '@r1-compass-library/features/validation-sync';
import { PlayValidationStateService } from '../play-validation-state.service';
import { validContentAssetsValidator } from '../play-validators';
import { rxEffect } from 'ngxtension/rx-effect';
import { FroalaImagesService } from '@r1-compass-library/services/froala/froala-images.service';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';

const usedIdsState = new Set<number>();
/**
 * @description The play's overall validity needs this state
 */
export const PlayTalkingPointsUsedAssetsStore = signalStore(
    withState({ usedContentLibraryIds: usedIdsState }),
    withMethods(store => ({
        setUsedContentLibraryIds: (usedIds: Set<number>) => {
            patchState(store, { usedContentLibraryIds: usedIds });
        },
    })),
    withDevtools('playValIds')
);

@Component({
    selector: 'r1-compass-app-play-talking-points',
    templateUrl: './play-talking-points.component.html',
    styles: `
        .component-footer {
            display: flex;
            flex-direction: row-reverse;
            padding-top: 10px;
            margin-right: 10px;
        }
        .no-data-view {
            font-size: var(--r1-font-12);
            font-style: italic;
            padding: 10px 0px;
        }
    `,
    imports: [
        InstructionsComponent,
        MatButtonModule,
        ReactiveFormsModule,
        ContentEditorComponent,
        FieldMappingComponent,
        FieldRowComponent,
        FieldFieldComponent,
        MatFormFieldModule,
        MatSelectModule,
    ],
    providers: [PlayTalkingPointsFormService],
})
export class PlayTalkingPointsComponent
    implements
        OnInit,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<PlaySectionName, Play, PlayValidationContext>
{
    readonly #dynamicContentStore = inject(DynamicContentStore);
    readonly #playStore = inject(PlayStore);
    readonly #froalaImagesService = inject(FroalaImagesService);
    readonly #destroyRef$ = inject(DestroyRef);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #snackBarService = inject(SnackBarService);
    readonly #route = inject(ActivatedRoute);
    readonly #formService = inject(PlayTalkingPointsFormService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #playValidationServiceStore = inject(PlayTalkingPointsUsedAssetsStore);
    readonly #validationService = inject(PlayValidationService);
    readonly #validationContextService = inject(PlayValidationContextService);
    readonly #validationStateService = inject(PlayValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #playValidationStateService = inject(PlayValidationStateService);

    readonly playId = inject(RoutingService).assetId;

    usedDataMartMergeFields: DataMartMergeField[] = [];
    usedPlayImporterFileHeaders: Set<string> = new Set();

    // `delegatedSignal`
    // Idea: This component can treat this delegated signal like any writable signal.
    // However, its value is sourced from the `source` in a store, and the source is updated
    // via the `update` when this component updates `usedContentAssetIds` with a `set` or `update`.
    //
    // In other words: Reads the source from the store for consumption here in this component, and updates the
    // store with the updater when this component updates this delegated signal.
    //
    // NOTE: Once we are on v22, this will be a `linkedSignal` with a `set` clause instead.
    // `delegatedSignal` was written by NgRx and then scrapped when Angular added `set` to `linkedSignal` in
    // Angular v22
    private usedContentAssetIds = delegatedSignal<Set<number>>({
        source: this.#playValidationServiceStore.usedContentLibraryIds,
        update: ids => this.#playValidationServiceStore.setUsedContentLibraryIds(ids),
    });
    usedImageFileNames: Set<string> = new Set();

    form = this.#formService.form;

    play = this.#playStore.play;
    isPlayImporter = computed<boolean>(() => this.play()?.type === 'Importer');
    playImporterFileHeaders = computed<string[]>(() => {
        if (this.isPlayImporter()) {
            return this.play()?.config?.playFileImport?.headers ?? [];
        }
        return [];
    });
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    protected blockSaveWhileTyping = signal(false); // See the love letter to Froala for context

    canDeactivate$ = this.#formService.canDeactivate$;
    saveGuardHandlerFn = this.#formService.saveGuardHandlerFn;
    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.play(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'talkingPoints',
            this.blockSaveWhileTyping()
        )
    );

    constructor() {
        this.saveGuardHandlerFn({
            form: this.#formService.form,
            canDeactivate$: this.canDeactivate$,
            params: injectParams('id'),
            entity$: this.#playStore.play$,
            savedSoCanRoute$: this.#formSaveWarningStore.savedSoCanRoute$,
            route: this.#route,
            disableSave: this.disableSave,
        });

        this.subscribeToValidationState(
            this.form,
            'talkingPoints',
            this.#validationService,
            this.#validationStateService
        );

        effect(
            () => {
                this.form.controls.froalaContentFormControl.addValidators(
                    validContentAssetsValidator(
                        () => this.usedContentAssetIds(),
                        () => this.#dynamicContentStore.libraryContent()
                    )
                );
                this.form.controls.froalaContentFormControl.updateValueAndValidity({ emitEvent: false });
            },
            { debugName: 'update froala content form control on used content asset ids or library content change' }
        );

        rxEffect(
            // update validation when context is ready on form status change
            combineLatest([
                this.form.statusChanges.pipe(debounceTime(250)),
                this.#validationContextService.context$.pipe(filterNil()),
            ]),
            ([_, context]) => {
                const validationStatus = this.#validationService.validateSection(this.form, 'talkingPoints', context);
                this.#validationStateService.setSectionInvalid(
                    'talkingPoints',
                    validationStatus.erroredSections.has('talkingPoints')
                );
            },
            { destroyRef: this.#destroyRef$ }
        );

        // Froala is Froala, so of course they bake in a delay when the form will emit events.
        // So the form events like:
        //     statusChanges (in the status workaround for updateValueAndValidity) (btw 250ms delay)
        //     valueChanges (in subscribeToValidationState) (btw 250ms delay)
        // Do not even account for this delay.
        //
        // It is possible to configure this setting apparently, but in lieu of messing with all of our
        // global Froala configuration after I chased down this terrible issue for an embarrasingly long time
        // and am not thinking correctly, I threw on a blanket delay on literally any DOM typing interaction
        // so we can lock the save button, on top of the 250ms delay from each of the status watchers.
        //
        // Otherwise, the invalid state could be out of sync, and the play's
        // validity is set out of sync on form save.
        const keyboardEvent$ = merge(
            fromEvent<KeyboardEvent>(document, 'keydown'),
            fromEvent<KeyboardEvent>(document, 'keyup')
        );
        rxEffect(
            keyboardEvent$.pipe(
                tap(() => this.blockSaveWhileTyping.set(true)),
                delay(1000)
            ),
            () => {
                this.blockSaveWhileTyping.set(false);
            }
        );
    }

    ngOnInit() {
        this.#dynamicContentStore.getDynamicContent();
        const playId = this.playId();
        if (playId && playId !== 'new' && this.#playStore.play()?.id !== Number(playId)) {
            this.#playStore.getPlay({ id: Number(playId) });
        }

        this.#dynamicContentStore
            .select(store => store.libraryContent)
            .pipe(
                filter(libraryContent => !!libraryContent && !!this.#playStore.play()), // Ensure content is loaded
                take(1), // Form initialization should only execute ONCE on initial load
                switchMap(() => {
                    const play = this.#playStore.play()!;
                    const rawTemplate = play.config?.talkingPointsDefinition?.talkingPointsTemplate ?? '';
                    const foundPlayId = String(play.id ?? this.playId());

                    return this.#froalaImagesService
                        .hydrateImagePlaceholders(rawTemplate, foundPlayId, 'play')
                        .pipe(map(hydratedTemplate => ({ play, hydratedTemplate })));
                }),
                takeUntilDestroyed(this.#destroyRef$)
            )
            .subscribe(({ play, hydratedTemplate }) => {
                const talkingPointsDefinition = play.config?.talkingPointsDefinition;
                const hydratedTalkingPointsDefinition = talkingPointsDefinition
                    ? { ...talkingPointsDefinition, talkingPointsTemplate: hydratedTemplate }
                    : undefined;

                this.#formService.initForm(hydratedTalkingPointsDefinition, play.config?.playFileImport?.headers ?? []);

                this.setUsedFieldMerges(hydratedTalkingPointsDefinition);

                // trueup whenever there's a change to the editor value
                this.form.controls.froalaContentFormControl.valueChanges
                    .pipe(debounceTime(100))
                    .subscribe(() => this.trueUpLookupFieldMappings());
            });

        this.#playStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#playStore.resetMessage();
        });

        // Whenever play or content assets are loaded, checked for deleted dependencies
        this.#dynamicContentStore.deletedLibraryContent$.subscribe(deletedContent => {
            this.warnForDeletedContentAssetsInUse(
                this.play()?.config?.talkingPointsDefinition?.contentAssets ?? [],
                deletedContent ?? []
            );
        });
    }

    onSave() {
        return this.save();
    }

    subscribeToValidationState(
        form: FormGroup<PlayTalkingPointsForm>,
        sectionName: PlaySectionName,
        validationService: ValidationService<PlaySectionName, Play, PlayFormMap, PlayValidationContext>,
        validationStateService: ValidationStateService<PlaySectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService,
            this.#validationContextService.context
        );
    }

    /**
     * Scans the Froala editor for all used field merges and tracks them.
     * Then if using play-importer, calls the form service to true-up the mappings for the lookup field names.
     */
    public trueUpLookupFieldMappings() {
        this.usedDataMartMergeFields = this.getUsedDataMartFieldMerges();
        this.usedPlayImporterFileHeaders = this.getUsedImporterFileHeaders();
        this.usedContentAssetIds.set(this.getDynamicContentFieldMerges());
        this.usedImageFileNames = this.#froalaImagesService.getUsedImages(document);
        if (this.isPlayImporter()) {
            this.#formService.trueUpLookupFieldMappings(this.getUsedLookupFieldNames(), this.playImporterFileHeaders());
        }
    }

    private setUsedFieldMerges(talkingPtsDefinition?: TalkingPointsDefinition) {
        this.usedDataMartMergeFields = talkingPtsDefinition?.dataMartFieldMerges ?? [];
        this.usedPlayImporterFileHeaders = new Set(talkingPtsDefinition?.importerFileHeaders ?? []);
        this.usedContentAssetIds.set(new Set(talkingPtsDefinition?.contentAssets.map(el => el.id) ?? []));
        this.usedImageFileNames = new Set(talkingPtsDefinition?.images ?? []);
    }

    /**
     * Gets the lookup field names for all data mart field merges that must be populated for this talkign points.
     * That includes both the field merges placed directly in the talking pts, AND the field merges contained
     * within an content assets on the talking pts.
     */
    private getUsedLookupFieldNames(): Set<string> {
        const talkingPtsLookupNames = this.usedDataMartMergeFields
            .filter(el => el.lookupFieldName)
            .map(el => el.lookupFieldName) as string[];
        const contentAssetLookupNames = this.getFieldMergesWithinContentAssets()
            .filter(el => el.lookupFieldName)
            .map(el => el.lookupFieldName) as string[];
        return new Set([...talkingPtsLookupNames, ...contentAssetLookupNames]);
    }

    private getFieldMergesWithinContentAssets() {
        const usedContentAssets = this.#dynamicContentStore
            .libraryContent()
            .filter(asset => this.usedContentAssetIds().has(asset.id));
        return usedContentAssets
            .flatMap(asset => asset.rules)
            .map(rule => rule?.dataMartFieldMerges)
            .filter(rule => !!rule)
            .flatMap(fieldMerge => fieldMerge);
    }

    private save() {
        const rawEditorHtml = this.form.controls.froalaContentFormControl.value;
        const normalizedTemplate = this.#froalaImagesService.normalizeHtmlForSave(rawEditorHtml);

        const config: Partial<PlayConfig> = {
            talkingPointsDefinition: {
                talkingPointsTemplate: normalizedTemplate,
                dataMartFieldMerges: this.usedDataMartMergeFields,
                contentAssets: Array.from(this.usedContentAssetIds()).map(id => ({ id: id }) as ContentAsset),
                importerFileHeaders: Array.from(this.usedPlayImporterFileHeaders),
                images: Array.from(this.usedImageFileNames),
                playImporterLookupFieldMappings: this.form.controls.playImporterLookupFieldMappings.getRawValue(),
            },
        };
        const play = {
            ...this.#playStore.play(),
            updatedBy: this.loggedInUserId(),
            lastConfigured: new Date(),
        };

        play.valid = this.#playValidationStateService.determinePlayValidity('talkingPoints', play);

        return this.#playStore.savePlayAndData({ play: play, config: config });
    }

    private getUsedDataMartFieldMerges() {
        const usedTfsFieldMerges: DataMartMergeField[] = [];
        const fieldMergeSpans = document
            .querySelector(`.${FieldMergeConstants.FR_ELEMENT}`)
            ?.querySelectorAll(`${FieldMergeConstants.SPAN}.${FieldMergeConstants.TFS_FIELD_MERGE}`);
        const fieldMergeDivs = document
            .querySelector(`.${FieldMergeConstants.FR_ELEMENT}`)
            ?.querySelectorAll(`${FieldMergeConstants.DIV}.${FieldMergeConstants.TFS_FIELD_MERGE}`);
        fieldMergeSpans?.forEach(span => {
            const options = span.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS) ?? '';
            const fieldMerge: DataMartMergeField = JSON.parse(options);
            const exists = usedTfsFieldMerges.find(existingFieldMerge => existingFieldMerge.name === fieldMerge.name);
            if (!exists) {
                usedTfsFieldMerges.push(fieldMerge);
            }
        });
        fieldMergeDivs?.forEach(div => {
            const options = div.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS) ?? '';
            const fieldMerge: DataMartMergeField = JSON.parse(options);
            const exists = usedTfsFieldMerges.find(existingFieldMerge => existingFieldMerge.name === fieldMerge.name);
            if (!exists) {
                usedTfsFieldMerges.push(fieldMerge);
            }
        });
        return usedTfsFieldMerges;
    }

    private getUsedImporterFileHeaders() {
        const usedFileHeaders: Set<string> = new Set();
        const fieldMergeSpans = document
            .querySelector(`.${FieldMergeConstants.FR_ELEMENT}`)
            ?.querySelectorAll(`${FieldMergeConstants.SPAN}.${FieldMergeConstants.FILE_HEADER_FIELD_MERGE}`);
        fieldMergeSpans?.forEach(span => usedFileHeaders.add(span.id));
        return usedFileHeaders;
    }

    private getDynamicContentFieldMerges(): Set<number> {
        const usedAssetIds: Set<number> = new Set();
        const fieldMergeSpans = document
            .querySelector(`.${FieldMergeConstants.FR_ELEMENT}`)
            ?.querySelectorAll(`${FieldMergeConstants.SPAN}.${FieldMergeConstants.DYNAMIC_CONTENT_FIELD_MERGE}`);
        fieldMergeSpans?.forEach(span => {
            const options = span.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS) ?? '';
            const asset: ContentAsset = JSON.parse(options); // This will only contain the ID.
            usedAssetIds.add(asset.id);
        });
        return usedAssetIds;
    }

    private warnForDeletedContentAssetsInUse(usedContentAssets: ContentAsset[], deletedContentAssets: ContentAsset[]) {
        const usedContentIds = new Set(usedContentAssets.map(c => c.id));
        const deletedContentIds = new Set(deletedContentAssets.map(c => c.id));
        const deletedContentIdsInUse = usedContentIds.intersection(deletedContentIds);
        if (deletedContentIdsInUse.size > 0) {
            const deletedContentAssetsInUse: ContentAsset[] = deletedContentAssets.filter(c =>
                deletedContentIdsInUse.has(c.id)
            );
            const message = `Your talking points uses the following deleted content assets: ${deletedContentAssetsInUse
                .map(c => `"${c.name}"`)
                .join(',')}`;
            this.#snackBarService.openGenericSnackbar({
                messages: [message],
                verticalPosition: 'top',
                panelClass: 'generic-snack-bar-warning',
                showDismissButton: true,
            });
        }
    }
}
